import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Mic,
  Users,
  Zap,
  CheckCircle,
  ArrowRight,
  Sparkles,
  FileText,
  ListTodo,
  MessageSquare,
  Search,
  Shield,
} from 'lucide-react';

const Landing = () => {
  const { t, isAuthenticated } = useApp();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-violet-500/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-violet-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                <span>AI-Powered Meeting Notes</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                {t('landing.hero.title')}
                <br />
                <span className="text-violet-600 dark:text-violet-400">{t('landing.hero.subtitle')}</span>
              </h1>

              <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-xl">
                {t('landing.hero.description')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  size="lg"
                  className="rounded-full bg-violet-600 hover:bg-violet-700 text-white px-8 h-12 text-base shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                  data-testid="hero-cta-button"
                >
                  <Link to={isAuthenticated ? '/dashboard' : '/login'}>
                    {t('landing.hero.cta')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="rounded-full px-8 h-12 text-base"
                  data-testid="hero-secondary-button"
                >
                  <a href="#features">{t('landing.hero.ctaSecondary')}</a>
                </Button>
              </div>
            </div>

            {/* Right content - Hero image/illustration */}
            <div className="relative lg:pl-8">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                  alt="Team meeting"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                
                {/* Floating cards */}
                <div className="absolute bottom-6 left-6 right-6 space-y-3">
                  <div className="glass rounded-xl p-4 backdrop-blur-xl bg-white/90 dark:bg-slate-900/90 shadow-lg fade-in">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">95%+ Accuracy</p>
                        <p className="text-xs text-muted-foreground">Transcription quality</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t('landing.features.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 group" data-testid="feature-card-presence">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Users className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{t('landing.features.presence.title')}</h3>
                <p className="text-muted-foreground leading-relaxed">{t('landing.features.presence.description')}</p>
              </CardContent>
            </Card>

            {/* Feature 2 */}
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 group" data-testid="feature-card-friction">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Zap className="w-7 h-7 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{t('landing.features.friction.title')}</h3>
                <p className="text-muted-foreground leading-relaxed">{t('landing.features.friction.description')}</p>
              </CardContent>
            </Card>

            {/* Feature 3 */}
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 group" data-testid="feature-card-clarity">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <CheckCircle className="w-7 h-7 text-violet-600 dark:text-violet-400" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{t('landing.features.clarity.title')}</h3>
                <p className="text-muted-foreground leading-relaxed">{t('landing.features.clarity.description')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-24 md:py-32 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                Everything you need from your meetings
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                ExpoTalk captures, transcribes, and analyzes your meetings with AI precision.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Instant Transcripts</h4>
                    <p className="text-muted-foreground text-sm">Get word-for-word transcriptions with speaker identification</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">AI Summaries</h4>
                    <p className="text-muted-foreground text-sm">Concise summaries highlighting key points and decisions</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                    <ListTodo className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Action Items</h4>
                    <p className="text-muted-foreground text-sm">Automatically extracted tasks and follow-ups</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">AI Assistant</h4>
                    <p className="text-muted-foreground text-sm">Ask questions about your meetings anytime</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center flex-shrink-0">
                    <Search className="w-6 h-6 text-pink-600 dark:text-pink-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Smart Search</h4>
                    <p className="text-muted-foreground text-sm">Find any discussion across all your meetings</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80"
                  alt="Meeting notes interface"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-violet-500/20 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 md:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {t('landing.howItWorks.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="text-center" data-testid="how-it-works-step-1">
              <div className="w-16 h-16 rounded-full bg-violet-600 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('landing.howItWorks.step1.title')}</h3>
              <p className="text-muted-foreground">{t('landing.howItWorks.step1.description')}</p>
            </div>

            {/* Step 2 */}
            <div className="text-center" data-testid="how-it-works-step-2">
              <div className="w-16 h-16 rounded-full bg-violet-600 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('landing.howItWorks.step2.title')}</h3>
              <p className="text-muted-foreground">{t('landing.howItWorks.step2.description')}</p>
            </div>

            {/* Step 3 */}
            <div className="text-center" data-testid="how-it-works-step-3">
              <div className="w-16 h-16 rounded-full bg-violet-600 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('landing.howItWorks.step3.title')}</h3>
              <p className="text-muted-foreground">{t('landing.howItWorks.step3.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 md:py-32 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {t('landing.useCases.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="overflow-hidden rounded-2xl border-0 shadow-lg hover:shadow-xl transition-all group" data-testid="use-case-client">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1560439514-4e9645039924?w=600&q=80"
                  alt="Client meeting"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-2">{t('landing.useCases.client.title')}</h3>
                <p className="text-muted-foreground">{t('landing.useCases.client.description')}</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden rounded-2xl border-0 shadow-lg hover:shadow-xl transition-all group" data-testid="use-case-research">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&q=80"
                  alt="Research interview"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-2">{t('landing.useCases.research.title')}</h3>
                <p className="text-muted-foreground">{t('landing.useCases.research.description')}</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden rounded-2xl border-0 shadow-lg hover:shadow-xl transition-all group" data-testid="use-case-team">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80"
                  alt="Team meeting"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-2">{t('landing.useCases.team.title')}</h3>
                <p className="text-muted-foreground">{t('landing.useCases.team.description')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-violet-600 to-violet-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            {t('landing.cta.title')}
          </h2>
          <p className="text-lg text-violet-100 mb-8 max-w-2xl mx-auto">
            {t('landing.cta.description')}
          </p>
          <Button
            asChild
            size="lg"
            className="rounded-full bg-white text-violet-700 hover:bg-violet-50 px-8 h-12 text-base shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            data-testid="cta-button"
          >
            <Link to={isAuthenticated ? '/dashboard' : '/login'}>
              {t('landing.cta.button')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
                <Mic className="w-4 h-4 text-white" />
              </div>
              <span className="text-white font-semibold">ExpoTalk</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
            </div>
            <p className="text-sm">© 2024 ExpoTalk. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
